import { useEffect, useRef } from 'react';

import etribeMark from '../assets/icons/etribe-mark.svg';
import { buttonVariants } from '../components/ui/Button';
import { useReducedMotion } from '../hooks/useMediaQuery';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  accent: boolean;
}

/* ---------- Particle network canvas ---------- */
function HeroNetwork() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const rafRef = useRef(0);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return; // No montar la animación si el usuario lo pidió.

    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const ctxMaybe = canvasEl.getContext?.('2d');
    if (!ctxMaybe) return; // jsdom / canvas unsupported → skip animation
    // Aliases no-null para que los closures internos no necesiten re-guardar.
    const canvas: HTMLCanvasElement = canvasEl;
    const ctx: CanvasRenderingContext2D = ctxMaybe;
    let particles: Particle[] = [];
    const dpr = Math.max(1, window.devicePixelRatio || 1);

    // Ajustamos el count según ancho de pantalla — mobile recibe menos partículas
    // para no saturar el main thread (O(n²) en el linker de partículas).
    const COUNT = window.innerWidth < 768 ? 24 : 40;
    const LINK_DIST = 130;
    const MOUSE_RADIUS = 160;
    let visible = true;

    function resize() {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function init() {
      const { width, height } = canvas.getBoundingClientRect();
      particles = Array.from({ length: COUNT }, (_, i) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 1.6 + 0.6,
        accent: i % 8 === 0,
      }));
    }

    function step() {
      if (!visible) {
        rafRef.current = requestAnimationFrame(step);
        return;
      }
      const { width, height } = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, width, height);

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width)  p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        if (mouseRef.current.active) {
          const dx = mouseRef.current.x - p.x;
          const dy = mouseRef.current.y - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < MOUSE_RADIUS) {
            const force = (1 - dist / MOUSE_RADIUS) * 0.6;
            p.x += (dx / dist) * force;
            p.y += (dy / dist) * force;
          }
        }

        if (p.accent) {
          ctx.beginPath();
          ctx.fillStyle = 'rgba(255, 200, 54, 0.18)';
          ctx.arc(p.x, p.y, p.r * 6, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.beginPath();
        ctx.fillStyle = p.accent ? '#FFC836' : 'rgba(255,255,255,0.85)';
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < LINK_DIST) {
            const alpha = (1 - dist / LINK_DIST) * 0.35;
            ctx.strokeStyle = a.accent || b.accent
              ? `rgba(255, 200, 54, ${alpha})`
              : `rgba(255, 255, 255, ${alpha * 0.55})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      rafRef.current = requestAnimationFrame(step);
    }

    function onPointerMove(e: PointerEvent) {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.active = true;
    }
    function onPointerLeave() {
      mouseRef.current.active = false;
      mouseRef.current.x = -9999;
      mouseRef.current.y = -9999;
    }
    function onResize() {
      cancelAnimationFrame(rafRef.current);
      resize();
      init();
      rafRef.current = requestAnimationFrame(step);
    }

    resize();
    init();
    rafRef.current = requestAnimationFrame(step);

    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerleave', onPointerLeave);
    window.addEventListener('resize', onResize);

    // Pausa el render cuando el canvas sale del viewport (libera CPU al hacer scroll).
    const io = new IntersectionObserver(
      ([entry]) => { visible = entry.isIntersecting; },
      { threshold: 0 },
    );
    io.observe(canvas);

    return () => {
      cancelAnimationFrame(rafRef.current);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('resize', onResize);
      io.disconnect();
    };
  }, [reducedMotion]);

  // Si reduced-motion, no renderear el canvas en absoluto.
  if (reducedMotion) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="absolute inset-0 w-full h-full"
    />
  );
}

/* ---------- Hero section ---------- */
export default function Hero() {
  return (
    <section
      id="top"
      className="relative bg-etribe-blue text-white overflow-hidden pt-[120px] pb-[140px] isolate"
    >
      {/* Glow gradients */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(80% 50% at 100% 0%, rgba(255,200,54,.10), transparent 60%), radial-gradient(60% 50% at 0% 100%, rgba(50,127,239,.18), transparent 60%)',
        }}
      />

      <div className="container-etribe relative">
        {/* Dots overlay */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,.08) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(circle at center, black 40%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(circle at center, black 40%, transparent 75%)',
          }}
        />

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center relative">
          {/* Copy */}
          <div>
            <p className="eyebrow !text-white/70">
              <span>Tribu digital · Consultora tecnológica</span>
            </p>
            <h1 className="text-[clamp(35px,5.5vw,96px)] leading-[1] mb-7">
              Tribu digital que convierte{' '}
              <span className="text-shimmer">datos en decisiones</span>
            </h1>
            <p className="text-[clamp(17px,1.3vw,20px)] text-white/80 max-w-[56ch] mb-10 leading-[1.55] text-justify hyphens-auto">
              Somos una consultora tecnológica especializada en Data, Analytics e Inteligencia
              Artificial. Diseñamos arquitecturas que llevan a las empresas líderes de México a
              operar en tiempo real.
            </p>
            <div className="flex flex-wrap gap-3.5">
              <a href="#contacto" className={buttonVariants({ variant: 'accent', size: 'lg' })}>
                Agenda una conversación
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>
              <a href="#casos" className={buttonVariants({ variant: 'outline-white', size: 'lg' })}>
                Conoce nuestros casos
              </a>
            </div>
          </div>

          {/* Visual — oculto en mobile/tablet; aparece desde lg */}
          <div className="relative aspect-square hidden lg:grid place-items-center w-full max-w-[640px] justify-self-center lg:justify-self-end">
            {/* Interactive particle network */}
            <HeroNetwork />

            {/* Pulse rings */}
            <div className="absolute inset-[22%] rounded-full border border-etribe-yellow/20 animate-pulse-ring pointer-events-none" aria-hidden />
            <div className="absolute inset-[18%] rounded-full border border-etribe-blue-soft/20 animate-pulse-ring [animation-delay:1.8s] pointer-events-none" aria-hidden />

            {/* Wordmark — escala con la columna, sin desbordar */}
            <div className="relative z-10 inline-flex items-end animate-float-iso drop-shadow-[0_30px_60px_rgba(16,3,163,.4)] pointer-events-none max-w-full px-4">
              <img
                src={etribeMark}
                alt="eTribe"
                className="w-full max-w-[520px] h-auto select-none"
                draggable={false}
              />
              <span className="w-3.5 h-3.5 rounded-full bg-etribe-yellow ml-3.5 mb-[0.22em] shadow-[0_0_24px_rgba(255,200,54,.7)] animate-blink flex-shrink-0" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
